import { Fragment, useEffect, useState } from 'react';
import type { CSSProperties, ReactNode, Ref } from 'react';

export type CrossfadePhase = 'entering' | 'exiting';

interface CrossfadeTransitionProps<Item> {
  item: Item | null;
  getKey: (item: Item) => string;
  durationMs: number;
  // Swap layers with no crossfade (e.g. swatch swap, where we want the change to be instant of a fade).
  skipFade?: boolean;
  className?: string;
  style?: CSSProperties;
  containerRef?: Ref<HTMLElement>;
  children: (item: Item, phase: CrossfadePhase) => ReactNode;
}

// Hold the outgoing layer slightly past the CSS fade so the unmount never clips it.
export const UNMOUNT_SAFETY_MS = 50;

interface Layers<Item> {
  current: Item | null;
  previous: Item | null;
}

/**
 * Generic two-layer crossfade: holds the outgoing item mounted beside the incoming one
 * so CSS can fade between them, dropping the old layer after `durationMs` (`skipFade`
 * skips the fade). The render prop gets each item with its `'entering' | 'exiting'` phase.
 * Shopbop reference: https://code.amazon.com/packages/ShopbopSharedReactComponents/blobs/5704ffa4ad6e41fe5e0ba058ea8704461c6f7741/--/packages/shared-react-components/src/Molecules/ProductTile/index.tsx#L313-L336
 */
export const CrossfadeTransition = <Item,>({
  item,
  getKey,
  durationMs,
  skipFade = false,
  className,
  style,
  containerRef,
  children
}: CrossfadeTransitionProps<Item>): JSX.Element => {
  const [layers, setLayers] = useState<Layers<Item>>(() => ({ current: item, previous: null }));

  const activeKey = item ? getKey(item) : null;
  const currentKey = layers.current ? getKey(layers.current) : null;

  // Derive the next layers during render when the item changes (getDerivedState pattern).
  if (activeKey !== currentKey) {
    setLayers(prev => (skipFade ? { current: item, previous: null } : { current: item, previous: prev.current }));
  }

  const previousKey = layers.previous ? getKey(layers.previous) : null;
  useEffect(() => {
    if (previousKey === null) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setLayers(prev => ({ ...prev, previous: null }));
    }, durationMs + UNMOUNT_SAFETY_MS);
    return () => clearTimeout(timer);
  }, [previousKey, durationMs]);

  return (
    <figure ref={containerRef} className={className} style={style}>
      {layers.current && <Fragment key={getKey(layers.current)}>{children(layers.current, 'entering')}</Fragment>}
      {layers.previous && previousKey !== currentKey && <Fragment key={getKey(layers.previous)}>{children(layers.previous, 'exiting')}</Fragment>}
    </figure>
  );
};
